import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { Banner } from '@shared/ui/feedback/Banner';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { useAuthStore } from '@shared/stores/authStore';
import { toApiError } from '@shared/api/errors';
import {
  completeAccessOnboarding,
  fetchAccessCredential,
  fetchAccessOnboardingContext,
  startAccessOnboarding,
  type AccessCredentialReveal,
} from './api';

/**
 * Visitor onboarding before access credentials — `/access/onboarding/:accessInviteId/:accessToken`.
 * Loads context, starts or resumes a flow instance, then navigates to `/onboarding?instance=…`.
 */
export function PublicAccessOnboarding(): JSX.Element | null {
  const navigate = useNavigate();
  const { accessInviteId = '', accessToken = '' } = useParams<{
    accessInviteId: string;
    accessToken: string;
  }>();
  const tenantId = useAuthStore((s) => s.tenantId);
  const [credential, setCredential] = useState<AccessCredentialReveal | null>(null);
  const [plainPin, setPlainPin] = useState<string | null>(null);
  const [qrUrls, setQrUrls] = useState<Record<string, string>>({});

  const contextQuery = useQuery({
    queryKey: ['access-onboarding-context', accessInviteId, accessToken],
    queryFn: async () => {
      const ctx = await fetchAccessOnboardingContext(accessInviteId, accessToken);
      const needsOnboarding = !!(ctx.require_onboarding_before_accept && ctx.onboarding_type_id);

      if (!needsOnboarding) {
        const complete = await completeAccessOnboarding(accessInviteId, accessToken);
        if (complete.pin) setPlainPin(String(complete.pin));
        const cred = await fetchAccessCredential(accessInviteId, accessToken);
        return { mode: 'credential' as const, cred };
      }

      if (ctx.flow_instance_id) {
        return { mode: 'flow' as const, flowInstanceId: ctx.flow_instance_id };
      }

      const start = await startAccessOnboarding(accessInviteId, accessToken);
      if (!start.flow_instance_id) {
        throw new Error('Could not start onboarding');
      }
      return { mode: 'flow' as const, flowInstanceId: start.flow_instance_id };
    },
    enabled: !!(accessInviteId && accessToken && tenantId),
    retry: false,
  });

  useEffect(() => {
    const result = contextQuery.data;
    if (!result || result.mode !== 'flow') return;
    navigate(`/onboarding?instance=${encodeURIComponent(result.flowInstanceId)}`, { replace: true });
  }, [contextQuery.data, navigate]);

  useEffect(() => {
    if (contextQuery.data?.mode !== 'credential') return;
    setCredential(contextQuery.data.cred);
  }, [contextQuery.data]);

  useEffect(() => {
    if (!credential) return;
    const rows =
      credential.issued_credentials?.length
        ? credential.issued_credentials
        : (credential.methods ?? []).map((method) => ({
            method: method.toLowerCase(),
            qr_payload: method.toLowerCase() === 'qr' ? credential.qr_payload ?? null : null,
          }));

    void (async () => {
      const next: Record<string, string> = {};
      for (const row of rows) {
        if (row.method === 'qr' && row.qr_payload) {
          try {
            next[row.method] = await QRCode.toDataURL(row.qr_payload, { width: 220, margin: 2 });
          } catch {
            /* skip */
          }
        }
      }
      setQrUrls(next);
    })();
  }, [credential]);

  if (!accessInviteId || !accessToken) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <Banner variant="danger">Invalid link.</Banner>
      </div>
    );
  }

  if (!tenantId) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <Banner variant="danger">Invalid link or missing tenant context. Reload the page.</Banner>
      </div>
    );
  }

  if (contextQuery.isLoading || contextQuery.data?.mode === 'flow') {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <Skeleton className="h-8 w-48" />
      </div>
    );
  }

  if (contextQuery.isError) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <Banner variant="danger">{toApiError(contextQuery.error).message}</Banner>
      </div>
    );
  }

  if (credential) {
    const rows =
      credential.issued_credentials?.length
        ? credential.issued_credentials
        : (credential.methods ?? []).map((method) => ({
            method: method.toLowerCase(),
            qr_payload: method.toLowerCase() === 'qr' ? credential.qr_payload ?? null : null,
          }));

    return (
      <div className="mx-auto max-w-lg px-6 py-10">
        <h1 className="font-display text-display-md text-ink mb-2">Your access details</h1>
        <p className="mb-6 text-body text-ink-secondary">Reference: {credential.reference}</p>
        <div className="space-y-4">
          {rows.map((row) => (
            <div key={row.method} className="rounded-xl bg-card p-4 hairline">
              <p className="mb-2 text-body font-medium text-ink">{row.method.toUpperCase()}</p>
              {(row.method === 'pin' || row.method === 'otp') && plainPin ? (
                <p className="font-mono text-2xl tracking-widest">{plainPin}</p>
              ) : null}
              {row.method === 'qr' && row.qr_payload && qrUrls.qr ? (
                <img src={qrUrls.qr} alt="Access QR code" className="mx-auto max-w-[220px]" />
              ) : null}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
}
